import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { CurrencyProvider } from "@/context/CurrencyContext";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import MapsPage from "@/pages/MapsPage";
import ShopCategoryPage from "@/pages/ShopCategoryPage";
import LearnPage from "@/pages/LearnPage";
import PlayPage from "@/pages/PlayPage";
import ProPage from "@/pages/ProPage";
import SearchPage from "@/pages/SearchPage";
import ProductPage from "@/pages/ProductPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import ThankYouPage from "@/pages/ThankYouPage";
import LegalPage from "@/pages/LegalPage";
import ProductFamilyPage from "@/pages/ProductFamilyPage";
import PortfolioPage from "@/pages/PortfolioPage";
import AppellationExplorePage from "@/pages/AppellationExplorePage";
import CepageExplorePage from "@/pages/CepageExplorePage";
import ProducerExplorePage from "@/pages/ProducerExplorePage";
import AcademyPage from "@/pages/AcademyPage";
import BusinessPage from "@/pages/BusinessPage";
import LibraryPage from "@/pages/LibraryPage";
// SEO/GEO pillar pages (2026-02) — capture long-tail LLM queries
// ("cartographie viticole", "géographie du vin", …) with rich static
// content + Schema.org (Article + BreadcrumbList + FAQPage).
import CartographieViticolePage from "@/pages/pillars/CartographieViticolePage";
import GeographieDuVinPage from "@/pages/pillars/GeographieDuVinPage";
import AppellationsViticolesPage from "@/pages/pillars/AppellationsViticolesPage";
import TerroirsViticolesPage from "@/pages/pillars/TerroirsViticolesPage";
import GlossairePage from "@/pages/GlossairePage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminCRMPage from "@/pages/admin/AdminCRMPage";
import AdminCRMContactPage from "@/pages/admin/AdminCRMContactPage";
import ContactDetailPage from "@/pages/admin/ContactDetailPage";
import AdminGelatoPage from "@/pages/admin/AdminGelatoPage";
import AdminGelatoProductsPage from "@/pages/admin/AdminGelatoProductsPage";
import AdminGoogleMerchantPage from "@/pages/admin/AdminGoogleMerchantPage";
import AdminCarteDuMoisPage from "@/pages/admin/AdminCarteDuMoisPage";
import AdminAppUsersPage from "@/pages/admin/AdminAppUsersPage";
import AdminAppSubscriptionsPage from "@/pages/admin/AdminAppSubscriptionsPage";
import AdminAppClaimsPage from "@/pages/admin/AdminAppClaimsPage";
import AdminAppAtlasPage from "@/pages/admin/AdminAppAtlasPage";
import AdminProducerLeadsPage from "@/pages/admin/AdminProducerLeadsPage";
import AdminAtlasBlocksPage from "@/pages/admin/AdminAtlasBlocksPage";
import AdminAppAcademyPage from "@/pages/admin/AdminAppAcademyPage";
import AdminAppAcademyNewPage from "@/pages/admin/AdminAppAcademyNewPage";
import AdminAppAcademyEditPage from "@/pages/admin/AdminAppAcademyEditPage";
import AdminAppShopOrdersPage from "@/pages/admin/AdminAppShopOrdersPage";
import AdminAppRagPage from "@/pages/admin/AdminAppRagPage";
import AdminStripeWebhookPage from "@/pages/admin/AdminStripeWebhookPage";
import AtlasProductPage from "@/pages/AtlasProductPage";
import AcademySeriesPage from "@/pages/AcademySeriesPage";
import AcademyWatchPage from "@/pages/AcademyWatchPage";
import AdminAcademySeriesPage from "@/pages/admin/AdminAcademySeriesPage";
import AdminBlogPage from "@/pages/admin/AdminBlogPage";
import AdminSocialPage from "@/pages/admin/AdminSocialPage";
import AdminEmailSequencesPage from "@/pages/admin/AdminEmailSequencesPage";
import AdminEmailTemplatesPage from "@/pages/admin/AdminEmailTemplatesPage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import BillingPortalPage from "@/pages/portal/BillingPortalPage";
import PortalAcademyPage from "@/pages/portal/PortalAcademyPage";
import PortalOrdersPage from "@/pages/portal/PortalOrdersPage";
import PrintSuccessPage from "@/pages/PrintSuccessPage";
import ShopPage from "@/pages/ShopPage";
import ShopProductPage from "@/pages/ShopProductPage";
import ContactPage from "@/pages/ContactPage";
import CartesPage from "@/pages/CartesPage";
import AdminNewsletterPage from "@/pages/admin/AdminNewsletterPage";
import AdminContactMessagesPage from "@/pages/admin/AdminContactMessagesPage";
import TerroirAIPage from "@/pages/AudreyAIPage";
import ReviewSubmitPage from "@/pages/ReviewSubmitPage";
import AdminShopReviewsPage from "@/pages/admin/AdminShopReviewsPage";
import FAQPage from "@/pages/FAQPage";
import AboutPage from "@/pages/AboutPage";
import AppPage from "@/pages/AppPage";
import NewsletterPage from "@/pages/NewsletterPage";
import ExperiencePage from "@/pages/ExperiencePage";
import AdminMonthlyThemePage from "@/pages/admin/AdminMonthlyThemePage";
import AdminAtlasFichesPage from "@/pages/admin/AdminAtlasFichesPage";
import AdminAtlasClaimsPage from "@/pages/admin/AdminAtlasClaimsPage";
import AdminInboundSuggestionsPage from "@/pages/admin/AdminInboundSuggestionsPage";
import AdminAtlasIAPage from "@/pages/admin/AdminAtlasIAPage";
import AdminPodcastPage from "@/pages/admin/AdminPodcastPage";
import AdminDashboardAppPage from "@/pages/admin/dashboard/AdminDashboardAppPage";
import AdminDashboardIframePage from "@/pages/admin/dashboard/AdminDashboardIframePage";
import AdminDashboardMapsPage from "@/pages/admin/dashboard/AdminDashboardMapsPage";
import AdminMonthlyCyclePage from "@/pages/admin/AdminMonthlyCyclePage";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";
import AdminSettingsEnvPage from "@/pages/admin/AdminSettingsEnvPage";

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <I18nProvider>
          <CurrencyProvider>
          <AuthProvider>
            <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/maps" element={<Navigate to="/shop" replace />} />
              <Route path="/books" element={<ShopCategoryPage category="books" />} />
              <Route path="/ebooks" element={<ShopCategoryPage category="ebooks" />} />
              <Route path="/courses" element={<ShopCategoryPage category="courses" />} />
              <Route path="/bundles" element={<ShopCategoryPage category="bundles" />} />
              <Route path="/products/:slug" element={<ProductFamilyPage />} />
              <Route path="/products/portfolio" element={<PortfolioPage />} />
              <Route path="/products/cellar" element={<Navigate to="/products/portfolio" replace />} />
              <Route path="/cellar" element={<Navigate to="/products/portfolio" replace />} />
              <Route path="/explore" element={<Navigate to="/blog" replace />} />
              <Route path="/explore/legacy" element={<Navigate to="/blog" replace />} />
              <Route path="/academy" element={<Navigate to="/app#academy" replace />} />
              <Route path="/academy/watch" element={<AcademyWatchPage />} />
              <Route path="/academy/series/:slug" element={<AcademySeriesPage />} />
              <Route path="/business" element={<BusinessPage />} />
              <Route path="/atlas" element={<Navigate to="/shop" replace />} />
              <Route path="/atlas/:variant" element={<AtlasProductPage />} />
              <Route path="/portfolio" element={<Navigate to="/products/portfolio" replace />} />
              <Route path="/tasting" element={<Navigate to="/products/tasting" replace />} />
              <Route path="/coach" element={<Navigate to="/app#coach" replace />} />
              <Route path="/learn" element={<Navigate to="/app#academy" replace />} />
              <Route path="/play" element={<Navigate to="/app#ai" replace />} />
              <Route path="/iframe" element={<Navigate to="/products/iframe" replace />} />
              <Route path="/pro" element={<ProPage />} />
              <Route path="/journal" element={<Navigate to="/blog" replace />} />
              <Route path="/journal/:slug" element={<Navigate to="/blog" replace />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/explore/:slug" element={<AppellationExplorePage />} />
              <Route path="/cepage/:slug" element={<CepageExplorePage />} />
              <Route path="/producteur/:slug" element={<ProducerExplorePage />} />
              <Route path="/blog/:slug" element={<BlogArticlePage />} />
              <Route path="/cartes" element={<CartesPage />} />
              <Route path="/maps-hub" element={<Navigate to="/cartes" replace />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/product/:slug" element={<Navigate to="/shop" replace />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/thank-you/:slug" element={<ThankYouPage />} />
              <Route path="/thank-you" element={<ThankYouPage />} />
              <Route path="/legal" element={<Navigate to="/legal/legal-notice" replace />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/portal/billing" element={<BillingPortalPage />} />
              <Route path="/avis/:token" element={<ReviewSubmitPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/about" element={<AboutPage />} />
              {/* GEO/SEO pillar pages */}
              <Route path="/cartographie-viticole" element={<CartographieViticolePage />} />
              <Route path="/geographie-du-vin" element={<GeographieDuVinPage />} />
              <Route path="/appellations-viticoles" element={<AppellationsViticolesPage />} />
              <Route path="/terroirs-viticoles" element={<TerroirsViticolesPage />} />
              <Route path="/glossaire" element={<GlossairePage />} />
              <Route path="/app" element={<AppPage />} />
              <Route path="/academy" element={<Navigate to="/app#academy" replace />} />
              <Route path="/geovinum-ai" element={<Navigate to="/app#ai" replace />} />
              <Route path="/audrey-ai" element={<Navigate to="/app#ai" replace />} />
              <Route path="/portal/academy" element={<PortalAcademyPage />} />
              <Route path="/portal/orders" element={<PortalOrdersPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/shop/:slug" element={<ShopProductPage />} />
              <Route path="/print/success" element={<PrintSuccessPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/newsletter" element={<NewsletterPage />} />
              <Route path="/experience" element={<ExperiencePage />} />
              <Route path="/products/experience" element={<ExperiencePage />} />
              <Route path="/terroir-ai" element={<Navigate to="/app#ai" replace />} />
              <Route path="/terroir" element={<Navigate to="/app#ai" replace />} />
              <Route path="/products/coach" element={<Navigate to="/app#coach" replace />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="dashboard/app" element={<AdminDashboardAppPage />} />
                <Route path="dashboard/iframe" element={<AdminDashboardIframePage />} />
                <Route path="dashboard/maps" element={<AdminDashboardMapsPage />} />
                <Route path="monthly-cycle" element={<AdminMonthlyCyclePage />} />
                <Route path="analytics" element={<AdminAnalyticsPage />} />
                <Route path="settings-env" element={<AdminSettingsEnvPage />} />
                <Route path="crm" element={<AdminCRMPage />} />
                <Route path="crm/:id" element={<AdminCRMContactPage />} />
                <Route path="crm/:id" element={<ContactDetailPage />} />
                <Route path="prints" element={<AdminGelatoPage />} />
                <Route path="gelato/products" element={<AdminGelatoProductsPage />} />
                <Route path="google-merchant" element={<AdminGoogleMerchantPage />} />
                <Route path="shop/carte-du-mois" element={<AdminCarteDuMoisPage />} />
                <Route path="shop/reviews" element={<AdminShopReviewsPage />} />
                <Route path="app-users" element={<AdminAppUsersPage />} />
                <Route path="app-subscriptions" element={<AdminAppSubscriptionsPage />} />
                <Route path="app-claims" element={<AdminAppClaimsPage />} />
                <Route path="app-atlas" element={<AdminAppAtlasPage />} />
                <Route path="producer-leads" element={<AdminProducerLeadsPage />} />
                <Route path="atlas-blocks/:entityType/:slug" element={<AdminAtlasBlocksPage />} />
                <Route path="app-academy" element={<AdminAppAcademyPage />} />
                <Route path="app-academy/new" element={<AdminAppAcademyNewPage />} />
                <Route path="app-academy/edit/:id" element={<AdminAppAcademyEditPage />} />
                <Route path="app-shop-orders" element={<AdminAppShopOrdersPage />} />
                <Route path="app-rag" element={<AdminAppRagPage />} />
                <Route path="stripe/webhook" element={<AdminStripeWebhookPage />} />
                <Route path="academy/series" element={<AdminAcademySeriesPage />} />
                <Route path="blog" element={<AdminBlogPage />} />
                <Route path="social" element={<AdminSocialPage />} />
                <Route path="email" element={<AdminEmailSequencesPage />} />
                <Route path="email/templates" element={<AdminEmailTemplatesPage />} />
                <Route path="newsletter" element={<AdminNewsletterPage />} />
                <Route path="monthly-theme" element={<AdminMonthlyThemePage />} />
                <Route path="atlas-ia" element={<AdminAtlasIAPage />} />
                <Route path="podcast" element={<AdminPodcastPage />} />
                <Route path="inbound/atlas-fiches" element={<AdminAtlasFichesPage />} />
                <Route path="inbound/claims" element={<AdminAtlasClaimsPage />} />
                <Route path="inbound/suggestions" element={<AdminInboundSuggestionsPage />} />
                <Route path="contact-messages" element={<AdminContactMessagesPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          </AuthProvider>
          </CurrencyProvider>
        </I18nProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
